import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VERSION = "2026-10-10";
const ONE_YEAR = 365 * 24 * 3600 * 1000;

const KEY = "fl_cookie_consent";

export const getCookieConsent = () => {
  try {
    const c = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!c || c.version !== VERSION || Date.now() - new Date(c.at).getTime() > ONE_YEAR) return null;
    return c;
  } catch { return null; }
};

const storeLocal = (c) => { localStorage.setItem(KEY, JSON.stringify(c)); window.dispatchEvent(new Event("fl:cookie-consent")); };

export const CookieBanner = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener("fl:open-cookie-banner", open);
    return () => window.removeEventListener("fl:open-cookie-banner", open);
  }, []);

  useEffect(() => {
    const local = getCookieConsent();
    if (local) {
      setVisible(false);
      if (user && !local.synced) axios.put(`${API}/consents/cookie`, { choice: local.choice, version: VERSION }, { withCredentials: true }).then(() => storeLocal({ ...local, synced: true })).catch(() => {});
      return;
    }
    if (!user) { setVisible(true); return; }
    axios.get(`${API}/consents/cookie`, { withCredentials: true }).then(r => {
      const c = r.data?.consent;
      if (c && c.version === VERSION) { storeLocal({ ...c, synced: true }); setVisible(false); }
      else setVisible(true);
    }).catch(() => setVisible(true));
  }, [user]);

  const decide = (choice) => {
    const c = { choice, at: new Date().toISOString(), version: VERSION, synced: false };
    storeLocal(c);
    setVisible(false);
    if (user) axios.put(`${API}/consents/cookie`, { choice, version: VERSION }, { withCredentials: true }).then(() => storeLocal({ ...c, synced: true })).catch(() => {});
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 p-4 sm:p-6 pointer-events-none" role="dialog" aria-live="polite" aria-label="Informativa cookie" data-testid="cookie-banner">
      <div className="pointer-events-auto max-w-3xl mx-auto bg-forest text-white rounded-2xl shadow-2xl border border-white/10 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <span className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center shrink-0"><Cookie className="h-5 w-5 text-brand-pink" /></span>
          <div className="text-sm leading-relaxed">
            <p className="font-semibold">Cookie tecnici e, solo con il tuo consenso, statistiche e marketing</p>
            <p className="text-white/75 text-xs sm:text-sm mt-0.5">
              I cookie tecnici servono per login, carrello e indirizzo di consegna. Con "Accetta" attivi anche Meta Pixel e Google tag per misurare le campagne; con "Rifiuta" restano solo quelli tecnici. Dettagli nella{" "}
              <Link to="/privacy/cookie_policy" className="underline underline-offset-2 text-white hover:text-brand-pink" data-testid="cookie-banner-policy-link">Cookie Policy</Link>.
            </p>
          </div>
        </div>
        <div className="flex gap-2 sm:flex-col sm:w-40">
          <Button onClick={() => decide("accepted")} className="flex-1 bg-brand-pink text-white hover:bg-brand-pink/90 rounded-full" data-testid="cookie-accept-btn">Accetta</Button>
          <Button onClick={() => decide("rejected")} variant="outline" className="flex-1 bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white rounded-full" data-testid="cookie-reject-btn">Rifiuta</Button>
        </div>
        <button onClick={() => decide("rejected")} className="absolute top-2 right-2 sm:hidden p-1 text-white/60" aria-label="Chiudi" data-testid="cookie-close-btn"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};
